import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PassiveSkill, PassiveSkillCategory, PassiveSkillMilestone } from '../../types';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { rateLimitedFirestoreService as firestoreService } from '../../services/rateLimitedFirestoreService';
import { passiveSkillService } from '../../services/passiveSkillService';
import { achievementService } from '../../services/achievementService';
import { subscriptionService } from '../../services/subscriptionService';
import PassiveSkillCard from '../PassiveSkillCard';
import CreatePassiveSkillModal from '../CreatePassiveSkillModal';
import PassiveSkillMilestoneModal from '../PassiveSkillMilestoneModal';
import { soundService } from '../../services/soundService';
import { trackPassiveSkillCreated, trackPassiveSkillMilestone } from '../../services/analyticsService';
import { getTitleForLevel } from '../../utils/levelSystem';

interface PassiveSkillsPageProps {
  onCreateModalChange?: (isOpen: boolean) => void;
  onMilestoneModalChange?: (isOpen: boolean) => void;
}

const PassiveSkillsPage: React.FC<PassiveSkillsPageProps> = ({
  onCreateModalChange,
  onMilestoneModalChange
}) => {
  const {
    profile,
    user,
    updateProfile,
    achievements,
    showNotification,
    setLevelUpQueue,
    passiveSkills: contextPassiveSkills,
    updatePassiveSkillInContext,
    addPassiveSkillToContext,
    removePassiveSkillFromContext,
    isDemoMode
  } = useUser();
  const { t } = useLanguage();
  const [filteredSkills, setFilteredSkills] = useState<PassiveSkill[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<PassiveSkillCategory | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState<PassiveSkillMilestone | null>(null);
  const [milestoneSkillName, setMilestoneSkillName] = useState('');
  const [completingSkillId, setCompletingSkillId] = useState<string | null>(null);
  
  const passiveSkills = contextPassiveSkills as PassiveSkill[];
  
  // Check if user can create more passive skills
  const activeSkillsCount = passiveSkills.filter(s => s.isActive).length;
  const canCreateMore = profile ? subscriptionService.canAddPassiveSkill(profile, activeSkillsCount).allowed : false;

  // Filter skills by category and active status
  useEffect(() => {
    let filtered = passiveSkills;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(skill => skill.category === selectedCategory);
    }

    if (!showInactive) {
      filtered = filtered.filter(skill => skill.isActive);
    }

    setFilteredSkills(filtered);
  }, [passiveSkills, selectedCategory, showInactive]);

  const totalBoosts = useMemo(() => {
    return passiveSkillService.calculateTotalPassiveBoosts(passiveSkills);
  }, [passiveSkills]);

  useEffect(() => {
    if (!profile) return;

    const currentBoosts = profile.passiveStatBoosts;
    const boostsChanged = 
      (currentBoosts.strength || 0) !== (totalBoosts.strength || 0) ||
      (currentBoosts.intelligence || 0) !== (totalBoosts.intelligence || 0) ||
      (currentBoosts.agility || 0) !== (totalBoosts.agility || 0) ||
      (currentBoosts.vitality || 0) !== (totalBoosts.vitality || 0) ||
      (currentBoosts.sense || 0) !== (totalBoosts.sense || 0);

    if (boostsChanged) {
      updateProfile({ passiveStatBoosts: totalBoosts });
    }
  }, [totalBoosts, profile?.passiveStatBoosts, updateProfile]);

  const checkAchievementsDebounced = useCallback(() => {
    if (passiveSkills.length === 0 || achievements.length === 0) return;

    const totalSkills = passiveSkills.length;
    const maxStreak = Math.max(...passiveSkills.map(s => s.longestStreak), 0);
    const maxLevel = Math.max(...passiveSkills.map(s => s.level), 0);
    const maxedSkillsCount = passiveSkills.filter(s => s.level === 10).length;
    
    const totalPassiveBoostPercent = (
      (totalBoosts.strength || 0) +
      (totalBoosts.intelligence || 0) +
      (totalBoosts.agility || 0) +
      (totalBoosts.vitality || 0) +
      (totalBoosts.sense || 0)
    ) * 100;

    // Calculate goal completion stats
    const goalCompletions = passiveSkills.reduce((acc, skill) => {
      if (skill.targetDuration) {
        const daysSinceCreation = Math.floor((Date.now() - skill.createdAt) / (1000 * 60 * 60 * 24));
        const isCompleted = daysSinceCreation >= skill.targetDuration;

        if (isCompleted) {
          acc.totalGoalsCompleted += 1;

          if (skill.targetDuration === 30) {
            acc.thirtyDayGoalsCompleted += 1;
          }

          // Check if it was a perfect goal (completed all expected sessions)
          let expectedCompletions = 0;
          if (skill.targetDuration) {
            switch (skill.frequency) {
              case 'daily':
                expectedCompletions = skill.targetDuration;
                break;
              case 'weekly':
                const weeks = Math.ceil(skill.targetDuration / 7);
                expectedCompletions = weeks * (skill.targetDaysPerWeek || 3);
                break;
              case 'custom':
                if (skill.allowedWeekdays && skill.allowedWeekdays.length > 0) {
                  for (let i = 0; i < skill.targetDuration; i++) {
                    const checkDate = new Date(skill.createdAt + i * 24 * 60 * 60 * 1000);
                    const dayOfWeek = checkDate.getDay();
                    if (skill.allowedWeekdays.includes(dayOfWeek)) {
                      expectedCompletions++;
                    }
                  }
                } else {
                  expectedCompletions = skill.targetDuration;
                }
                break;
            }
          }

          if (skill.totalCompletions >= expectedCompletions) {
            acc.perfectGoalsCompleted += 1;
          }
        }
      }
      return acc;
    }, {
      totalGoalsCompleted: 0,
      thirtyDayGoalsCompleted: 0,
      perfectGoalsCompleted: 0
    });

    const { updatedAchievements, newlyUnlocked } = achievementService.checkPassiveSkillAchievements(
      achievements,
      totalSkills,
      maxStreak,
      maxLevel,
      maxedSkillsCount,
      totalPassiveBoostPercent,
      goalCompletions
    );

    if (newlyUnlocked.length > 0 && user) {
      firestoreService.saveAchievements(user.uid, updatedAchievements);
    }
  }, [passiveSkills, achievements, totalBoosts, user?.uid]);

  const achievementTrigger = useMemo(() => {
    return `${passiveSkills.length}_${Math.max(...passiveSkills.map(s => s.level), 0)}`;
  }, [passiveSkills]);

  useEffect(() => {
    checkAchievementsDebounced();
  }, [achievementTrigger, checkAchievementsDebounced]);

  // Notify parent component when modals open/close
  useEffect(() => {
    onCreateModalChange?.(isCreateModalOpen);
  }, [isCreateModalOpen, onCreateModalChange]);

  useEffect(() => {
    onMilestoneModalChange?.(currentMilestone !== null);
  }, [currentMilestone, onMilestoneModalChange]);

  const needsSanitization = (text: string): boolean => {
    const suspiciousPatterns = [
      /<[^>]*>/,
      /javascript:/i,
      /on\w+\s*=/i,
      /<script/i,
      /http[s]?:\/\//,
    ];
    return suspiciousPatterns.some(pattern => pattern.test(text));
  };

  const handleCreateSkill = async (skill: Omit<PassiveSkill, 'id' | 'userId'>) => {
    if (!profile) return;
    
    try {
      const limitCheck = subscriptionService.canAddPassiveSkill(profile, activeSkillsCount);
      
      if (!limitCheck.allowed) {
        showNotification(limitCheck.reason || 'You\'ve reached your passive skills limit', 'warning');
        setIsCreateModalOpen(false);
        return;
      }
      
      if (!skill.name || skill.name.trim().length === 0) {
        showNotification('❌ Passive skill name cannot be empty', 'warning');
        return;
      }
      if (skill.name.length > 100) {
        showNotification('❌ Passive skill name is too long (max 100 characters)', 'warning');
        return;
      }
      if (skill.description && skill.description.length > 500) {
        showNotification('❌ Description is too long (max 500 characters)', 'warning');
        return;
      }
      
      let sanitizedName = skill.name.trim();
      let sanitizedDescription = skill.description?.trim() || '';
      
      if (needsSanitization(skill.name) || needsSanitization(skill.description || '')) {
        const { inputSanitizationService } = await import('../../services/inputSanitizationService');
        const sanitizeResult = await inputSanitizationService.sanitizePassiveSkillInput(
          skill.name,
          skill.description || ''
        );
        
        if (sanitizeResult.warnings.length > 0) {
          showNotification('⚠️ Input was cleaned for security', 'warning');
        }
        
        sanitizedName = sanitizeResult.name;
        sanitizedDescription = sanitizeResult.description;
      }
      
      const newSkill: Omit<PassiveSkill, 'id'> = {
        ...skill,
        name: sanitizedName,
        description: sanitizedDescription,
        userId: user?.uid || 'demo_user',
      };

      let savedSkill: PassiveSkill;
      
      if (user) {
        const skillId = await firestoreService.savePassiveSkill(user.uid, newSkill);
        savedSkill = { ...newSkill, id: skillId } as PassiveSkill;
      } else {
        const skillId = `demo_skill_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        savedSkill = { ...newSkill, id: skillId } as PassiveSkill;
      }
      
      addPassiveSkillToContext(savedSkill);
      trackPassiveSkillCreated(sanitizedName, skill.category);
      
      const features = subscriptionService.getCurrentFeatures(profile);
      const newActiveCount = activeSkillsCount + 1;
      const remainingText = features.maxActivePassiveSkills === 'unlimited' 
        ? 'Unlimited slots available' 
        : `${features.maxActivePassiveSkills - newActiveCount} slots remaining`;
      
      showNotification(
        `✨ Passive Skill "${sanitizedName}" created! ${remainingText}`,
        'success'
      );
      
      setIsCreateModalOpen(false);
      soundService.trainingEstablished();
    } catch (error) {
      console.error('Error creating passive skill:', error);
      showNotification('❌ Failed to create passive skill', 'warning');
    }
  };

  const handleCompleteSkill = async (skillId: string) => {
    if (completingSkillId === skillId) return;
    
    const skill = passiveSkills.find(s => s.id === skillId);
    if (!skill) return;

    if (!passiveSkillService.canCompleteToday(skill)) {
      return;
    }

    try {
      setCompletingSkillId(skillId);
      
      const { updatedSkill, leveledUp, milestone } = passiveSkillService.completeSkill(skill);
      
      if (!isDemoMode) {
        await firestoreService.updatePassiveSkill(skillId, updatedSkill);
      }
      
      updatePassiveSkillInContext(skillId, updatedSkill);

      if (milestone) {
        setCurrentMilestone(milestone);
        setMilestoneSkillName(skill.name);
        soundService.trainingComplete();
      } else {
        soundService.trainingComplete();
      }
    } catch (error) {
      console.error('Error completing skill:', error);
      showNotification('❌ Failed to complete skill', 'warning');
    } finally {
      setCompletingSkillId(null);
    }
  };

  const handleClaimMilestone = async () => {
    if (!currentMilestone || !profile) return;

    try {
      const reward = currentMilestone.reward;
      let xpGained = 0;
      let currencyGained = 0;
      let statPointsGained = 0;

      if (reward.xp) xpGained = reward.xp;
      if (reward.currency) currencyGained = reward.currency;
      if (reward.statPoints) statPointsGained = reward.statPoints;

      const updates: any = {
        totalXP: profile.totalXP + xpGained,
        currency: profile.currency + currencyGained,
        unallocatedStatPoints: profile.unallocatedStatPoints + statPointsGained,
      };

      if (xpGained > 0) {
        let currentXP = profile.currentXP + xpGained;
        let currentLevel = profile.level;
        let xpToNextLevel = profile.xpToNextLevel;
        let additionalStatPoints = 0;
        const levelUpResults: any[] = [];

        while (currentXP >= xpToNextLevel) {
          currentXP -= xpToNextLevel;
          currentLevel++;
          const statPointsFromLevelUp = 3;
          additionalStatPoints += statPointsFromLevelUp;

          levelUpResults.push({
            newLevel: currentLevel,
            statIncreases: {}, // No auto stat allocation - player chooses
            newTitle: getTitleForLevel(currentLevel)
          });

          xpToNextLevel = 100 + (currentLevel - 1) * 50;
        }

        updates.currentXP = currentXP;
        updates.level = currentLevel;
        updates.xpToNextLevel = xpToNextLevel;
        updates.unallocatedStatPoints = profile.unallocatedStatPoints + statPointsGained + additionalStatPoints;

        // Update current title if leveled up
        if (levelUpResults.length > 0) {
          updates.currentTitle = levelUpResults[levelUpResults.length - 1].newTitle;

          // Queue level-up notifications with delay to avoid interfering with training complete sound
          setTimeout(() => {
            setLevelUpQueue(prev => [...prev, ...levelUpResults]);
          }, 1500); // 1.5 second delay
        }
      }

      updateProfile(updates);

      const skill = passiveSkills.find(s => s.name === milestoneSkillName);
      if (skill) {
        const daysToReach = Math.floor((Date.now() - skill.createdAt) / (1000 * 60 * 60 * 24));
        trackPassiveSkillMilestone(milestoneSkillName, currentMilestone.value, daysToReach);
      }

      setCurrentMilestone(null);
      setMilestoneSkillName('');
      soundService.trainingComplete();
    } catch (error) {
      console.error('Error claiming milestone reward:', error);
    }
  };

  const handleToggleActive = async (skillId: string) => {
    const skill = passiveSkills.find(s => s.id === skillId);
    if (!skill) return;

    if (isDemoMode) {
      showNotification('🔒 Sign up to pause/resume passive skills and save your progress!', 'warning');
      return;
    }

    try {
      await firestoreService.updatePassiveSkill(skillId, { isActive: !skill.isActive });
      updatePassiveSkillInContext(skillId, { isActive: !skill.isActive });
    } catch (error) {
      console.error('Error toggling skill:', error);
      showNotification('❌ Failed to toggle skill', 'warning');
    }
  };

  const handleDeleteSkill = async (skillId: string) => {
    if (isDemoMode) {
      showNotification('🔒 Sign up to delete passive skills and save your progress!', 'warning');
      return;
    }

    if (!window.confirm(t.passiveSkills.deleteConfirm)) {
      return;
    }

    try {
      await firestoreService.deletePassiveSkill(skillId);
      removePassiveSkillFromContext(skillId);
      soundService.trainingTerminated();
    } catch (error) {
      console.error('Error deleting skill:', error);
      showNotification('❌ Failed to delete skill', 'warning');
    }
  };

  const categories: Array<{ id: PassiveSkillCategory | 'all'; label: string; icon: string; color: string }> = [
    { id: 'all', label: t.passiveSkills.all, icon: '⭐', color: 'blue' },
    { id: 'physical', label: t.passiveSkills.physical, icon: '⚔️', color: 'red' },
    { id: 'mental', label: t.passiveSkills.mental, icon: '📚', color: 'purple' },
    { id: 'social', label: t.passiveSkills.social, icon: '🤝', color: 'green' },
    { id: 'spiritual', label: t.passiveSkills.spiritual, icon: '🧘', color: 'cyan' },
    { id: 'productivity', label: t.passiveSkills.productivity, icon: '⚡', color: 'yellow' },
  ];

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-4 sm:px-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="font-orbitron text-2xl tracking-wider text-blue-400 uppercase">
                ［ {t.passiveSkills.title} ］
              </span>
            </div>
            <p className="text-sm text-gray-400 font-rajdhani">
              {t.passiveSkills.subtitle}
            </p>
          </div>
          
          <button
            onClick={() => {
              if (canCreateMore) {
                setIsCreateModalOpen(true);
              } else {
                const features = subscriptionService.getCurrentFeatures(profile!);
                showNotification(
                  `⚠️ You've reached your limit of ${features.maxActivePassiveSkills} active passive skills. Upgrade to S-Rank for unlimited skills!`,
                  'warning'
                );
              }
            }}
            disabled={!canCreateMore}
            className={`relative group border-t-2 border-l-2 border-r-2 border-b-2 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 font-orbitron font-bold flex items-center gap-2 justify-center text-xs sm:text-sm transition-all ${
              canCreateMore
                ? 'bg-gradient-to-b from-blue-800/50 via-cyan-800/40 to-blue-950/50 border-blue-500/50 border-r-cyan-700/70 border-b-cyan-700/70 hover:border-blue-500/60 hover:scale-105 text-white'
                : 'bg-gradient-to-b from-gray-900/40 to-gray-950/50 border-gray-700/60 text-gray-400 cursor-not-allowed opacity-60'
            }`}
            style={canCreateMore ? {
              boxShadow: '0 6px 18px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(59, 130, 246, 0.3), inset 0 1px 3px rgba(100, 200, 255, 0.15), inset 0 -2px 6px rgba(30, 58, 138, 0.4)'
            } : {
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.04), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            <span className="tracking-wider text-sm uppercase relative z-10">
              {canCreateMore ? t.passiveSkills.newSkill : '🔒 Limit Reached'}
            </span>
            {canCreateMore && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
            )}
          </button>
        </div>

        {/* Active Passive Boosts Display */}
        {Object.values(totalBoosts).some(boost => (boost || 0) > 0) && (
          <div 
            className="bg-gradient-to-b from-emerald-950/40 via-green-950/40 to-emerald-950/60 backdrop-blur-sm border-t-2 border-l-2 border-emerald-500/50 border-r-2 border-b-2 border-r-emerald-700/70 border-b-emerald-700/70 rounded-3xl p-4"
            style={{
              boxShadow: '0 6px 18px rgba(16, 185, 129, 0.4), 0 2px 6px rgba(16, 185, 129, 0.3), inset 0 1px 3px rgba(52, 211, 153, 0.15), inset 0 -2px 6px rgba(5, 78, 59, 0.4)'
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <img 
                src="/icons/icon-passive-active-boost.png" 
                alt="Active Boosts"
                className="w-6 h-6 object-contain"
                style={{ filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.8))' }}
              />
              <h3 className="text-lg font-orbitron font-bold text-emerald-400 uppercase tracking-wider">
                {t.passiveSkills.activeBoosts}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(totalBoosts).map(([stat, boost]) => {
                if (!boost || boost === 0) return null;
                return (
                  <div 
                    key={stat} 
                    className="flex items-center gap-2 bg-gradient-to-b from-black/40 to-black/50 px-3 py-1.5 rounded-2xl border-2 border-t-emerald-500/30 border-l-emerald-500/30 border-r-emerald-600/50 border-b-emerald-700/60"
                    style={{
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.2), inset 0 1px 2px rgba(52, 211, 153, 0.1)'
                    }}
                  >
                    <span className="text-white font-rajdhani capitalize text-sm">{stat}:</span>
                    <span className="text-emerald-400 font-orbitron font-bold text-sm">+{(boost * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Category Filters */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3 sm:mb-4">
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
          <span className="font-orbitron text-sm tracking-wider text-blue-400 uppercase">
            {t.passiveSkills.filter}
          </span>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-2xl font-orbitron font-bold uppercase text-[10px] sm:text-xs transition-all relative border-t-2 border-l-2 border-r-2 border-b-2 ${
                selectedCategory === cat.id
                  ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105'
                  : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:border-blue-500/50 hover:scale-105'
              }`}
              style={selectedCategory === cat.id ? {
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(100, 150, 255, 0.15), inset 0 -1px 3px rgba(30, 58, 138, 0.4)'
              } : {
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              <span className="mr-2 relative z-10">{cat.icon}</span>
              <span className="relative z-10">{cat.label}</span>
              {selectedCategory === cat.id && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
              )}
            </button>
          ))}
        </div>

        {/* Show Inactive Toggle */}
        <div className="flex items-center gap-1 sm:gap-3 mt-3 sm:mt-4">
          <input
            type="checkbox"
            id="showInactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-3 h-3 sm:w-4 sm:h-4 accent-blue-600 cursor-pointer transform scale-[0.65] sm:scale-100"
          />
          <label htmlFor="showInactive" className="text-gray-400 font-rajdhani text-[10px] sm:text-sm uppercase cursor-pointer">
            {t.passiveSkills.showPaused}
          </label>
        </div>
      </div>

      {/* Skills Grid */}
      {filteredSkills.length === 0 ? (
        <div className="text-center py-14 sm:py-20 px-4">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-xl sm:text-2xl font-orbitron font-bold text-white mb-2">{t.passiveSkills.noSkills}</h3>
          <p className="text-gray-400 font-rajdhani text-base sm:text-lg mb-6 max-w-md mx-auto">
            {selectedCategory !== 'all' 
              ? t.passiveSkills.noSkillsCategory.replace('{category}', categories.find(c => c.id === selectedCategory)?.label.toLowerCase() || selectedCategory)
              : t.passiveSkills.noSkillsDesc
            }
          </p>
          <button
            onClick={() => {
              if (canCreateMore) {
                setIsCreateModalOpen(true);
              } else {
                const features = subscriptionService.getCurrentFeatures(profile!);
                showNotification(
                  `⚠️ You've reached your limit of ${features.maxActivePassiveSkills} active passive skills. Upgrade to S-Rank for unlimited skills!`,
                  'warning'
                );
              }
            }}
            disabled={!canCreateMore}
            className={`font-orbitron font-bold py-2 sm:py-2.5 px-4 sm:px-5 rounded-2xl uppercase tracking-wider transition-all text-xs sm:text-sm border-t-2 border-l-2 border-r-2 border-b-2 relative ${
              canCreateMore
                ? 'bg-gradient-to-b from-blue-800/60 via-cyan-800/50 to-blue-950/60 border-blue-500/50 border-r-cyan-700/70 border-b-cyan-700/70 text-white hover:scale-105'
                : 'bg-gradient-to-b from-gray-900/40 to-gray-950/50 border-gray-700/60 text-gray-400 cursor-not-allowed opacity-60'
            }`}
            style={canCreateMore ? {
              boxShadow: '0 6px 18px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(59, 130, 246, 0.3), inset 0 1px 3px rgba(100, 200, 255, 0.15), inset 0 -2px 6px rgba(30, 58, 138, 0.4)'
            } : {
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.04), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            <span className="relative z-10">{canCreateMore ? t.passiveSkills.createFirst : '🔒 Limit Reached'}</span>
            {canCreateMore && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
            )}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 px-3 sm:px-0">
          {filteredSkills.map(skill => (
            <PassiveSkillCard
              key={skill.id}
              skill={skill}
              onComplete={() => handleCompleteSkill(skill.id)}
              onToggleActive={() => handleToggleActive(skill.id)}
              onDelete={() => handleDeleteSkill(skill.id)}
            />
          ))}
        </div>
      )}

      {/* Create Skill Modal */}
      <CreatePassiveSkillModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateSkill}
      />

      {/* Milestone Modal */}
      <PassiveSkillMilestoneModal
        isOpen={currentMilestone !== null}
        milestone={currentMilestone}
        skillName={milestoneSkillName}
        onClaim={handleClaimMilestone}
      />
    </div>
  );
};

export default PassiveSkillsPage;
